import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { GiConfirmed } from 'react-icons/gi';
import { toast } from 'react-toastify';
import { useBookingContext } from '../../../contexts/BookingProvider';
import useAppointmentActions from '../../../hooks/APIHooks/appointments/useAppointmentActions';
import { appointmentSlotSchema } from '../../../schemas';
import {
  AppointmentSlot,
  AvailableAssociate,
} from '../../../types/appointments';
import { convertMinutesToHours } from '../../../utils/timeAndDate';
import BookingDetailsTable from '../../AppointmentsComps/BookingComps/BookingDetailsTable';
import InputFieldBadge from '../Badges/InputFieldBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTimeSlot from './ModalTimeSlot';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';
import SlotBookingConsentModal from './SlotBookingConsentModal';

const AvailableSlotsModal = ({
  date,
  associate,
  partialTimeSlots,
  handleModalClose,
  handleAdditionalModalClose,
}: {
  date: string;
  associate: AvailableAssociate;
  partialTimeSlots?: Array<AppointmentSlot>;
  handleModalClose: () => void;
  handleAdditionalModalClose?: () => void;
}) => {
  // integration of react hooks here
  const [showConsentModal, setShowConsentModal] = useState<boolean>(false);

  const [availableSlots, setAvailableSlots] = useState<Array<AppointmentSlot>>(
    []
  );
  const [maxMinIndex, setMaxMinIndex] = useState<Array<number>>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<
    Array<{ index: number; slot: string }>
  >([]);

  const [selectedSlotDetails, setSelectedSlotDetails] = useState<any>();

  // integration of context hooks here
  const { serviceId, setPartialTimeSlots, timePeriod } = useBookingContext();

  // integration of custom API hooks here
  const { getAvailableSlotsHandler } = useAppointmentActions('');

  console.log('APPOINTMENT DATE: ', date, associate.id, serviceId);

  // fetching available time slots here
  useEffect(() => {
    if (partialTimeSlots?.length) {
      setAvailableSlots(partialTimeSlots);
    } else if (associate?.id && date) {
      getAvailableSlotsHandler
        .mutateAsync({
          body: {
            associate_id: associate.id,
            service_id: serviceId,
            date: date,
            current_time: new Date().toLocaleTimeString('en-GB', {
              hour12: false,
            }),
          },
        })
        .then((res) => {
          if (res.is_success) {
            if (timePeriod.slot_start_time) {
              const availableSlotsInTimePeriod = res.data.filter(
                (slot) =>
                  +slot.start_time.split(':')[0] >=
                    +timePeriod.slot_start_time.split(':')[0] &&
                  +slot.start_time.split(':')[0] <=
                    +timePeriod.slot_end_time.split(':')[0]
              );

              setAvailableSlots(availableSlotsInTimePeriod);
            } else {
              setAvailableSlots(res.data);
            }
          }
        })
        .catch((error) => {
          console.log(error);
          setAvailableSlots([]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associate.id, partialTimeSlots]);

  // sorting selected appointments based on index here
  useEffect(() => {
    if (selectedTimeSlots.length) {
      const sortedTimeSlots = selectedTimeSlots.sort(
        (slotA, slotB) => slotA.index - slotB.index
      );

      setSelectedTimeSlots(sortedTimeSlots);

      // setting max & min index for disabling here
      setMaxMinIndex([
        sortedTimeSlots[0].index,
        sortedTimeSlots[sortedTimeSlots.length - 1].index,
      ]);
    }
  }, [selectedTimeSlots]);

  // handler function to pass selected slot details to consent modal declared here
  const availableSlotFormHandler = (values: any) => {
    setShowConsentModal(true);
    console.log('VALUES: ', values);
    setSelectedSlotDetails({
      time_slot: `${selectedTimeSlots[0].slot.split(' - ')[0]} - ${
        selectedTimeSlots[selectedTimeSlots.length - 1].slot.split(' - ')[1]
      }`,
      want_reminder: values.want_reminder,
      reminder_time: values.reminder_time,
    });
  };

  // handler function to update array on select time slot declared here
  const onSelectTimeSlotHandler = (index: number, slotDuration: string) => {
    if (selectedTimeSlots.length) {
      if (selectedTimeSlots.filter((slot) => slot.index === index).length) {
        const prevTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index - 1
        );
        const nextTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index + 1
        );

        if (
          typeof prevTimeSlot?.index === 'number' &&
          typeof nextTimeSlot?.index === 'number'
        ) {
          toast.warn('Cannot Deselect This Slot!!');
        } else {
          setSelectedTimeSlots((state) =>
            state.filter((slot) => slot.index !== index)
          );
        }
      } else {
        setSelectedTimeSlots((state) => [
          ...state,
          { index, slot: slotDuration },
        ]);
      }
    } else {
      setSelectedTimeSlots((state) => [
        ...state,
        { index, slot: slotDuration },
      ]);
    }
  };

  // function to check if time slot selected or not declared here
  const isTimeSlotSelected = (index: number): boolean => {
    return !!selectedTimeSlots.filter((slot) => slot.index === index).length;
  };

  // rendering available slots modal component here
  return (
    <>
      <ParentModal
        onClick={() => {
          handleModalClose();
          setPartialTimeSlots([]);
        }}
        customWidthClass='md:w-[750px]'
      >
        <div className='flex flex-col items-center justify-center gap-1'>
          <ModalTitle titleText='available slots' />

          <p className='text-sm text-legal-dark-blue mr-4 italic font-semibold'>
            (For <span>{moment(date).format('DD-MM-YYYY')}</span>)
          </p>
        </div>

        <Formik
          initialValues={{
            time_slot: '',
            want_reminder: false,
            reminder_time: '',
          }}
          validationSchema={appointmentSlotSchema}
          onSubmit={availableSlotFormHandler}
        >
          {(props) => (
            <Form className='space-y-5'>
              {availableSlots.length ? (
                <>
                  <div className='grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4'>
                    <div className='h-[430px] space-y-4 overflow-y-auto p-2 academia-scrollbar border-2 border-dashed border-base-cyan rounded-lg'>
                      {availableSlots.map((slot, index) => (
                        <ModalTimeSlot
                          key={index}
                          index={index}
                          id={`time-slot-${index}`}
                          name={`time-slot-${index}`}
                          slot={slot}
                          onSelect={onSelectTimeSlotHandler}
                          isSlotSelected={isTimeSlotSelected}
                          isDisabled={
                            selectedTimeSlots.length
                              ? !(
                                  index >= maxMinIndex[0] - 1 &&
                                  index <=
                                    maxMinIndex[maxMinIndex.length - 1] + 1
                                )
                              : false
                          }
                        />
                      ))}
                    </div>

                    <div className='flex items-center'>
                      <BookingDetailsTable
                        selectedTimeSlotCount={selectedTimeSlots.length}
                        creditsPerAppointment={
                          associate.credits_per_appointment
                        }
                        slotDuration={associate.slot_duration}
                        appointmentBufferTime={
                          associate.appointment_buffer_time
                        }
                      />
                    </div>
                  </div>

                  <p className='font-semibold text-xs text-base-red !mt-2'>
                    **Each additional slot adds{' '}
                    {associate.appointment_buffer_time} minutes and costs{' '}
                    {(
                      (associate.credits_per_appointment /
                        associate.slot_duration) *
                      associate.appointment_buffer_time
                    ).toLocaleString('en-US', {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 1,
                    })}{' '}
                    extra credits.**
                  </p>
                </>
              ) : (
                <p className='text-base-red font-medium text-center text-lg'>
                  No Slots Found!!
                </p>
              )}

              <motion.span
                initial={{ y: -20, opacity: 0, display: 'none' }}
                animate={
                  props.touched.time_slot && props.errors.time_slot
                    ? { y: 0, opacity: 1, display: 'block' }
                    : { y: -20, opacity: 0, transitionEnd: { display: 'none' } }
                }
                className='block mt-1 text-base-red text-base'
              >
                {props.errors.time_slot}
              </motion.span>

              {availableSlots.length ? (
                <>
                  <AcademiaCheckboxField
                    name='want_reminder'
                    label='Want custom reminder?'
                  />

                  <AnimatePresence
                    initial={false}
                    mode='wait'
                    onExitComplete={() => null}
                  >
                    {props.values.want_reminder ? (
                      <motion.div
                        initial={{ y: -50, opacity: 0, height: 0 }}
                        animate={{ y: 0, opacity: 1, height: 'fit-content' }}
                        exit={{ y: -50, opacity: 0, height: 0 }}
                      >
                        <AcademiaInputField
                          type='number'
                          name='reminder_time'
                          label='Reminder Time (Minutes)'
                          placeholder='Enter reminder time here... (Max 120)'
                          badge={
                            props.values.reminder_time ? (
                              <InputFieldBadge
                                badgeText=''
                                badgeValue={convertMinutesToHours(
                                  +props.values.reminder_time
                                )}
                                isValid={!props.errors.reminder_time}
                              />
                            ) : undefined
                          }
                          isRequired
                        />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>

                  <AcademiaButton
                    title={
                      !selectedTimeSlots.length
                        ? 'Please, select at least one time slot first!'
                        : undefined
                    }
                    type='submit'
                    variant='premium'
                    additionalClassNames='!py-2'
                    icon={<GiConfirmed />}
                    fullWidth
                    isDisabled={!selectedTimeSlots.length}
                  >
                    Confirm Slot
                  </AcademiaButton>
                </>
              ) : null}
            </Form>
          )}
        </Formik>
      </ParentModal>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showConsentModal ? (
          <SlotBookingConsentModal
            associate={associate as AvailableAssociate}
            slotDetails={selectedSlotDetails}
            slotCount={selectedTimeSlots.length}
            handleModalClose={() => setShowConsentModal(false)}
            handleAdditionalModalClose={() => {
              handleModalClose();
              handleAdditionalModalClose && handleAdditionalModalClose();
            }}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default AvailableSlotsModal;
